import {
  DrawerDescription,
  FormDatePicker,
  TooltipContent,
  TooltipTrigger,
  DrawerContent,
  DrawerTrigger,
  DrawerHeader,
  ComboboxItem,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormSelect,
  FormInput,
  Tooltip,
  Drawer,
  Button,
  Label,
  Form,
} from '@ui/components';
import { ColorOptions } from '@pages/employees-profile/components';
import { FormAssignTable } from '@shared/components/assign-table';
import { UnsavedChangesDialog } from '@shared/components';
import { PropsWithChildren, useState, FC } from 'react';
import { Cancel, Info } from '@ui/components/icons';
import { formatPhoneNumber } from '@helpers/utils';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { Masks } from '@helpers/constant';
import { DateTime } from 'luxon';

import { FormCreateEmployeesProfileServices } from './create-employees-profile-services/create-employees-profile-services';
import { useCreateEmployeesProfile } from './use-create-employees-profile';

export const CreateEmployeesProfileDrawer: FC<PropsWithChildren> = ({ children }) => {
  const [hasAnyConfigurationEditing, setHasAnyConfigurationEditing] = useState<boolean>(false);
  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState<boolean>(false);
  const [open, onOpenChange] = useState<boolean>(false);
  const { currency } = useCurrentUser();

  const { assignedEmployees, assignTable, isPending, onSubmit, form } = useCreateEmployeesProfile({
    onSuccess: () => {
      form.reset();
      onOpenChange(false);
    },
  });

  const handleSubmit = () => {
    if (hasAnyConfigurationEditing) {
      setIsUnsavedChangesDialogOpen(true);
      return;
    }
    form.handleSubmit(() => onSubmit())();
  };

  const validFrom = form.watch('validFrom');
  const validTo = form.watch('validTo');
  const { t, i18n } = useTranslation();

  return (
    <Drawer
      onOpenChange={(value) => {
        form.reset();
        onOpenChange(value);
      }}
      open={open}
      handleOnly
    >
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle>
            {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.ADD_PROFILE_HEADER')}
          </DrawerTitle>
          <DrawerDescription />
          <DrawerClose asChild>
            <Button variant="ghost" size="small" icon>
              <Cancel />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        <form className="flex-1 overflow-auto flex flex-col p-4 pt-5 gap-2">
          <div className="flex-1">
            <div className="space-y-4">
              <Form {...form}>
                <div className="grid grid-cols-6 gap-2 items-center border-b border-border-weak pb-4">
                  <Label className="col-span-2" htmlFor="name">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.PROFILE_NAME')}
                    </Typography>
                  </Label>
                  <div className="col-span-4 flex items-center">
                    <FormInput
                      placeholder={t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.TYPE_IN_NAME')}
                      componentProps={{ id: 'name' }}
                      className="w-full col-span-4"
                      formControl={form.control}
                      size="small"
                      name="name"
                      required
                    />
                  </div>

                  <Label className="col-span-2" htmlFor="color">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.COLOR')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center">
                    <FormSelect
                      placeholder={t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.SELECT_COLOR')}
                      className="w-full col-span-4"
                      formControl={form.control}
                      options={ColorOptions()}
                      size="small"
                      name="color"
                    />
                  </div>

                  <Label className="col-span-2" htmlFor="validFrom">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.VALID_FROM')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center gap-2">
                    <FormDatePicker
                      disabled={{
                        before: new Date(),
                        after: validTo
                          ? DateTime.fromJSDate(validTo).minus({ days: 1 }).endOf('day').toJSDate()
                          : undefined,
                      }}
                      placeholder={t(
                        'EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.TYPE_IN_DATE_OPTIONAL',
                      )}
                      componentProps={{ id: 'validFrom', lang: i18n.language }}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="validFrom"
                      size="small"
                    />

                    <Button
                      onClick={() => form.setValue('validFrom', null)}
                      disabled={!validFrom}
                      variant="ghost"
                      type="button"
                      icon
                    >
                      <Cancel />
                    </Button>
                  </div>

                  <Label className="col-span-2" htmlFor="validTo">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.VALID_TO')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center gap-2">
                    <FormDatePicker
                      disabled={{
                        before: DateTime.fromJSDate(validFrom || new Date())
                          .plus({ days: 1 })
                          .startOf('day')
                          .toJSDate(),
                      }}
                      placeholder={t(
                        'EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.TYPE_IN_DATE_OPTIONAL',
                      )}
                      componentProps={{ id: 'validTo', lang: i18n.language }}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="validTo"
                      size="small"
                    />

                    <Button
                      onClick={() => form.setValue('validTo', null)}
                      disabled={!validTo}
                      variant="ghost"
                      type="button"
                      icon
                    >
                      <Cancel />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-2 items-center border-b border-border-weak pb-4">
                  <div className="col-span-6 flex gap-2 items-center">
                    <Typography variant="labelMediumBold" element="span">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.MAX_AMOUNT')}
                    </Typography>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="text-weak" />
                      </TooltipTrigger>
                      <TooltipContent alignOffset={50} side="bottom">
                        {t('EMPLOYEE_PROFILES.MAX_AMOUNT_TOOLTIP')}
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <Label className="col-span-2" htmlFor="maxAmount">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.AMOUNT')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center">
                    <FormInput
                      trailingIcon={{
                        position: 'right',
                        icon: (
                          <Typography className="text-content-weak" variant="labelSmallBold">
                            {currency}
                          </Typography>
                        ),
                      }}
                      componentProps={{
                        id: 'maxAmount',
                        maskOptions: { mask: Masks.CURRENCY_FORMAT },
                      }}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="maxAmount"
                      size="small"
                    />
                  </div>
                </div>

                <div className="border-b border-border-weak pb-4">
                  <Typography variant="labelMediumBold" element="span">
                    {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.EMPLOYEES_ASSIGNED_TO_PROFILE')}
                  </Typography>

                  <FormAssignTable
                    text={{
                      comboboxPlaceholder: t(
                        'EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.COMBOBOX_PLACEHOLDER',
                      ),
                      comboboxNoResults: t(
                        'EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.COMBOBOX_NO_RESULTS',
                      ),
                      searchPlaceholder: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.SEARCH_PLACEHOLDER'),
                      tableNoResults: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.TABLE_NO_RESULTS'),
                      removeButtonText: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.REMOVE'),
                      assignButtonText: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.ADD'),
                      title: t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.TABLE_TITLE'),
                      selectedRows: (count) =>
                        t('EMPLOYEE_PROFILES.EMPLOYEES_ASSIGN.SELECTED_ROWS', {
                          postProcess: 'interval',
                          count,
                        }),
                    }}
                    itemTemplate={(item) => (
                      <ComboboxItem
                        keywords={[item.firstName || '', item.lastName || '', item.phoneNumber]}
                        disabled={assignedEmployees?.includes(item.id)}
                        className="justify-between h-12"
                        label={item.id}
                        value={item.id}
                        key={item.id}
                      >
                        <Typography variant="labelMediumBook">
                          {item.firstName ?? '-'} {item.lastName ?? '-'}
                        </Typography>
                        <Typography variant="labelMediumBook">
                          {formatPhoneNumber(item.phoneNumber, item.phoneExt)}
                        </Typography>
                      </ComboboxItem>
                    )}
                    formControl={form.control}
                    name="assignedEmployees"
                    className="mt-4"
                    {...assignTable}
                  />
                </div>

                <Typography variant="labelMediumBold" element="span">
                  {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.SELECT_SERVICES')}
                </Typography>

                <FormCreateEmployeesProfileServices
                  setHasAnyConfigurationEditing={setHasAnyConfigurationEditing}
                  formControl={form.control}
                  className="mt-4"
                  name="services"
                />

                <UnsavedChangesDialog
                  onSave={form.handleSubmit(() => onSubmit())}
                  setIsOpen={setIsUnsavedChangesDialogOpen}
                  isOpen={isUnsavedChangesDialogOpen}
                  isPending={isPending}
                />
              </Form>
            </div>
          </div>
          <Button
            className="shrink-0 mt-4"
            onClick={handleSubmit}
            isLoading={isPending}
            disabled={isPending}
            type="button"
            size="large"
            wide
          >
            {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.CREATE_PROFILE_SUBMIT')}
          </Button>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
